
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, Phone, Mail, Building, Calendar, Users } from "lucide-react";
import { OrganizationWithRelations } from "@/types/organization.types";

interface BasicInfoCardProps {
  organization: OrganizationWithRelations;
}

// Helper function to parse structured description data
function parseDescription(description: string) {
  if (!description) return { summary: "", assignments: {}, notes: "" };
  
  // Check for structured format like "Account: N/A, Sr Account: N/A, Project Management: N/A"
  const assignmentPattern = /(\w+[\s\w]*?):\s*([^,]+)/g;
  const assignments: Record<string, string> = {};
  let summary = description;
  
  // Extract assignment information
  let match;
  while ((match = assignmentPattern.exec(description)) !== null) {
    const role = match[1].trim();
    const person = match[2].trim();
    assignments[role] = person;
  }
  
  // If we found assignments, clean the summary
  if (Object.keys(assignments).length > 0) {
    summary = description.replace(/(\w+[\s\w]*?):\s*([^,]+),?\s*/g, '').trim();
  }
  
  // Handle other common patterns
  if (summary.includes('Added automatically for email domain')) {
    const domainMatch = summary.match(/Added automatically for email domain (.+)/);
    if (domainMatch) {
      summary = `Organization created from email domain: ${domainMatch[1]}`;
    }
  }
  
  if (summary.includes('is a') && summary.includes('organization added to the CRM')) {
    const parts = summary.split(' is a ');
    if (parts.length === 2) {
      const [name, rest] = parts;
      const typeMatch = rest.match(/(.+?) organization added to the CRM on (.+)\./);
      if (typeMatch) {
        summary = `${typeMatch[1]} organization since ${typeMatch[2]}`;
      }
    }
  }
  
  return { summary, assignments, notes: "" };
}

export function BasicInfoCard({ organization }: BasicInfoCardProps) {
  const parsedDescription = parseDescription(organization.description || "");
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building className="h-5 w-5" />
          Basic Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status and Tier Row */}
        <div className="flex gap-2 mb-4">
          {organization.status && (
            <Badge variant={organization.status === 'active' ? 'default' : 'secondary'}>
              {organization.status}
            </Badge>
          )}
          {organization.tier && (
            <Badge variant="outline">
              {organization.tier} Tier
            </Badge>
          )}
          {organization.client_type && (
            <Badge variant="secondary">
              {organization.client_type}
            </Badge>
          )}
        </div>
        
        {/* Core Information */}
        <div className="grid grid-cols-1 gap-2">
          {organization.industry && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground">Industry:</span>
              <span className="text-sm font-medium">{organization.industry}</span>
            </div>
          )}
          
          {organization.size && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Users className="h-4 w-4 mr-1" /> Company Size:
              </span>
              <span className="text-sm font-medium">{organization.size}</span>
            </div>
          )}

          {organization.annual_revenue && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground">Annual Revenue:</span>
              <span className="text-sm font-medium">${organization.annual_revenue.toLocaleString()}</span>
            </div>
          )}

          {organization.address && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground">Address:</span>
              <span className="text-sm font-medium truncate max-w-[200px]">{organization.address}</span>
            </div>
          )}
          
          {organization.start_date && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Calendar className="h-4 w-4 mr-1" /> Client Since:
              </span>
              <span className="text-sm font-medium">
                {new Date(organization.start_date).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        {/* Contact Information */}
        <div className="border-t pt-4 space-y-2">
          {organization.website && (
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Globe className="h-4 w-4 mr-1" /> Website:
              </span>
              <a
                href={organization.website.startsWith('http') ? organization.website : `https://${organization.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-blue-500 hover:text-blue-700 truncate max-w-[200px]"
              >
                {organization.website}
              </a>
            </div>
          )}

          {organization.phone && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Phone className="h-4 w-4 mr-1" /> Phone:
              </span>
              <span className="text-sm font-medium">{organization.phone}</span>
            </div>
          )}

          {organization.email && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-muted-foreground flex items-center">
                <Mail className="h-4 w-4 mr-1" /> Email:
              </span>
              <a
                href={`mailto:${organization.email}`}
                className="text-sm font-medium text-blue-500 hover:text-blue-700"
              >
                {organization.email}
              </a>
            </div>
          )}
        </div>

        {/* Account Assignments */}
        {Object.keys(parsedDescription.assignments).length > 0 && (
          <div className="border-t pt-4">
            <span className="text-sm font-medium text-muted-foreground">Account Assignments:</span>
            <div className="mt-2 space-y-1">
              {Object.entries(parsedDescription.assignments).map(([role, person]) => (
                <div key={role} className="flex justify-between">
                  <span className="text-sm text-muted-foreground">{role}:</span>
                  <span className="text-sm font-medium">{person === 'N/A' ? 'Unassigned' : person}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Parsed Description Summary */}
        {parsedDescription.summary && parsedDescription.summary !== organization.description && (
          <div className="border-t pt-4">
            <span className="text-sm font-medium text-muted-foreground">Overview:</span>
            <p className="text-sm mt-1 leading-relaxed">{parsedDescription.summary}</p>
          </div>
        )}
        
        {organization.referral_source && (
          <div className="border-t pt-4">
            <span className="text-sm font-medium text-muted-foreground">Referral Source:</span>
            <p className="text-sm mt-1">{organization.referral_source}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
